import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Spinner,
  Flex,
  Button,
  Checkbox,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
  Tooltip,
  ModalFooter,
  VStack,
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { RiSearchLine } from "react-icons/ri";
import { ChevronDownIcon, DeleteIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { MdDirectionsRun } from "react-icons/md";
import { listMetrics } from "../../services/MetricService";
import { Header } from "build-ts/metadata";
import { getSortByLabel } from "../../utils/analysisUtils";
import showToast from "hooks/useCustomToast";
import { searchPlansheaders, deletePlan } from "services/AnalysisPlanServices";
import CustomModal from "../modals/CustomModal";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { useUser } from "context/UserContext";

interface LibraryPlansComponentProps {
  onSelectionChange?: (selectedItems: Set<string>) => void;
  selectedItems?: Set<string>;
  hideActions?: boolean;
}

const LibraryPlansComponent: React.FC<LibraryPlansComponentProps> = ({
  onSelectionChange,
  selectedItems: externalSelectedItems,
  hideActions = false
}) => {
  const { defaultTimeZone } = useUser();
  const [plans, setPlans] = useState<Header[]>([]);
  const [metricHeaders, setMetricHeaders] = useState<Header[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchString, setSearchString] = useState("");
  const [submittedSearchString, setSubmittedSearchString] = useState("");
  const [sortBy, setSortBy] = useState("CREATED");
  const [sortAscending, setSortAscending] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedPlans, setSelectedPlans] = useState<Set<string>>(externalSelectedItems || new Set());
  const [searchMetrics, setSearchMetrics] = useState<string[]>([]);

  // Delete modal states
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openDependencyModal, setOpenDependencyModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [dependencies, setDependencies] = useState<Header[]>([]);

  const pageSize = 20;

  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await searchPlansheaders(pageSize, currentPage, sortBy, sortAscending, submittedSearchString, searchMetrics, false, "");
      if(response) {
        setPlans(response.headers);
        setTotalRows(response.totalRows);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      showToast("Error fetching plans", "Failed to load analysis plans", "error");
    } finally {
      setIsLoading(false);
    }
  }, [pageSize, currentPage, sortBy, sortAscending, submittedSearchString, searchMetrics]);

  useEffect(() => {
    fetchMetricHeaders();
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Sync external selection changes
  useEffect(() => {
    if (externalSelectedItems) {
      setSelectedPlans(externalSelectedItems);
    }
  }, [externalSelectedItems]);

  const fetchMetricHeaders = async () => {
    try {
      const data = await listMetrics(""); // Pass empty string as modelId
      if (data) {
        setMetricHeaders(data);
      }
    } catch (error) {
      console.error("Error fetching metric headers:", error);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSubmittedSearchString(searchString);
      setCurrentPage(1); // Reset to first page when searching
    }
  };

  const handleMetricChange = (metricId: string) => {
    setSearchMetrics((prevMetrics) => {
      const newMetrics = prevMetrics.includes(metricId)
        ? prevMetrics.filter((id) => id !== metricId)
        : [...prevMetrics, metricId];
      return newMetrics;
    });
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSelectAllMetrics = () => {
    if (searchMetrics.length === metricHeaders.length) {
      // If all are selected, deselect all
      setSearchMetrics([]);
    } else {
      // Select all metrics
      const allMetricIds = metricHeaders.map(header => header.id!);
      setSearchMetrics(allMetricIds);
    }
    setCurrentPage(1); // Reset to first page when filtering
  };

  const formatDate = (dateMs: number | undefined) => {
    if (!dateMs) return "N/A";
    try {
      return `${epochConverterWithTimeZone(dateMs || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} ${defaultTimeZone.toUpperCase()}`;
    } catch {
      return "N/A";
    }
  };

  // Handle individual plan selection
  const handlePlanSelection = (planId: string) => {
    const newSelectedPlans = new Set(selectedPlans);
    if (newSelectedPlans.has(planId)) {
      newSelectedPlans.delete(planId);
    } else {
      newSelectedPlans.add(planId);
    }
    setSelectedPlans(newSelectedPlans);
    onSelectionChange?.(newSelectedPlans);
  };

  // Handle select all/none
  const handleSelectAll = () => {
    const newSelectedPlans = selectedPlans.size === plans.length
      ? new Set<string>()
      : new Set(plans.map(plan => plan.id!));
    setSelectedPlans(newSelectedPlans);
    onSelectionChange?.(newSelectedPlans);
  };

  // Check if all plans are selected
  const isAllSelected = plans.length > 0 && selectedPlans.size === plans.length;
  const isIndeterminate = selectedPlans.size > 0 && selectedPlans.size < plans.length;

  const handleDeletePlan = async (id: string) => {
    setOpenDeleteModal(true);
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (deleteId) {
      const response = await deletePlan(deleteId);

      // Close the confirmation modal
      setOpenDeleteModal(false);

      if(response && response.ok){
        showToast("Plan deleted successfully", "", "success");
        fetchPlans();
      } else if(response.length > 0){
        setDependencies(response);
        setOpenDependencyModal(true);
      } else {
        showToast("Error deleting plan", "Failed to delete plan", "error");
      }
      // Reset delete state
      setDeleteId(null);
    }
  };

  return (
    <Box p={6} height="100%" overflowY="auto">
      {/* Search and Filter Section */}
      <HStack spacing={4} mb={6} justify="space-between">
        <HStack spacing={4}>
          <InputGroup size="md" maxW="400px">
            <InputLeftElement>
              <Icon as={RiSearchLine} color="gray.500" />
            </InputLeftElement>
            <Input
              placeholder="Search plans..."
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              focusBorderColor="purple.600"
            />
          </InputGroup>

          {/* Filter by Metric */}
          <Flex align="center">
            <Text
              align="center"
              mb={0}
              mr={2}
              fontSize="sm"
              fontWeight="medium"
              color="fg.muted"
              whiteSpace="nowrap"
            >
              Filter by:
            </Text>
            <Menu closeOnSelect={false} preventOverflow={true}>
              <MenuButton
                as={Button}
                color="fg.default"
                bg="bg.default"
                maxW="100%"
                width="auto"
                rightIcon={<ChevronDownIcon color="fg.muted" />}
                fontSize="sm"
                size="md"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
                fontWeight="normal"
                _hover={{ bg: "purple.50" }}
                p={3}
              >
                {metricHeaders && metricHeaders.length > 0
                  ? `Selected (${searchMetrics.length})`
                  : "Select Metrics"}
              </MenuButton>
              <MenuList maxHeight="200px" overflowY="auto" ml={-6}>
                {/* Select All Option */}
                <MenuItem minH="40px" zIndex={10} borderBottom="1px" borderColor="gray.200">
                  <Checkbox
                    isChecked={metricHeaders && searchMetrics.length === metricHeaders.length}
                    isIndeterminate={metricHeaders && searchMetrics.length > 0 && searchMetrics.length < metricHeaders.length}
                    onChange={handleSelectAllMetrics}
                    colorScheme="purple"
                    fontWeight="semibold"
                  >
                    Select All
                  </Checkbox>
                </MenuItem>
                {metricHeaders &&
                  metricHeaders.map((header: Header, index) => (
                    <MenuItem key={index} minH="40px" zIndex={10}>
                      <Checkbox
                        isChecked={searchMetrics.includes(header.id!)}
                        onChange={() => handleMetricChange(header.id!)}
                        colorScheme="purple"
                      >
                        {header.name}
                      </Checkbox>
                    </MenuItem>
                  ))}
              </MenuList>
            </Menu>
          </Flex>

          {/* Sort By Dropdown */}
          <Flex align="center">
            <Text
              align="center"
              mb={0}
              mr={2}
              fontSize="sm"
              fontWeight="medium"
              color="fg.muted"
              whiteSpace="nowrap"
            >
              Sort by:
            </Text>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon color="fg.muted" />}
                fontSize="xs"
                size="xs"
                rounded="md"
                color="fg.default"
                bg="bg.default"
                border="1px"
                borderColor="gray.300"
                _hover={{ bg: "purple.50" }}
                p={3}
                aria-label="Sort by"
                textAlign="left"
                width="150px"
                isTruncated
              >
                {getSortByLabel(sortBy)} {sortAscending ? "Asc" : "Desc"}
              </MenuButton>
              <MenuList>
                <MenuItem key="last_modified_asc"
                  onClick={() => {
                    setSortBy("LAST_MODIFIED");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Last Modified (Asc)
                </MenuItem>
                <MenuItem key="last_modified_desc"
                  onClick={() => {
                    setSortBy("LAST_MODIFIED");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Last Modified (Desc)
                </MenuItem>
                <MenuItem key="created_asc"
                  onClick={() => {
                    setSortBy("CREATED");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Created At (Asc)
                </MenuItem>
                <MenuItem key="created_desc"
                  onClick={() => {
                    setSortBy("CREATED");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Created At (Desc)
                </MenuItem>
                <MenuItem key="name_asc"
                  onClick={() => {
                    setSortBy("NAME");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Name (Asc)
                </MenuItem>
                <MenuItem key="name_desc"
                  onClick={() => {
                    setSortBy("NAME");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Name (Desc)
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
          {selectedPlans.size > 0 && !hideActions && (
            <Button
              colorScheme="purple"
              size="md"
              onClick={() => {
                // Open all selected plans in new tabs
                selectedPlans.forEach(planId => {
                  window.open(`/plan/${planId}`, "_blank");
                });
              }}
              isDisabled={selectedPlans.size === 0}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed"
              }}
            >
              Open {selectedPlans.size > 0 ? `(${selectedPlans.size})` : ""}
            </Button>
          )}
        </HStack>

        <HStack spacing={4}>
          {selectedPlans.size > 0 && !hideActions && (
            <Text fontSize="sm" color="purple.600" fontWeight="medium" m={0}>
              {selectedPlans.size} selected
            </Text>
          )}
          <Text fontSize="sm" color="fg.muted" mb={0}>
            {plans.length} results
          </Text>
        </HStack>
      </HStack>

      {/* Table */}
      <TableContainer
        bg="white"
        borderRadius="lg"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
      >
        {isLoading ? (
          <Flex justify="center" align="center" p={8}>
            <Spinner size="lg" color="purple.500" />
          </Flex>
        ) : (
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm" width="50px">
                  <Checkbox
                    isChecked={isAllSelected}
                    isIndeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    colorScheme="purple"
                  />
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Plan Name
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Description
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Author
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Created Date
                </Th>
                {!hideActions && (
                  <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                    {/* for delete */}
                  </Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {plans.length === 0 ? (
                <Tr>
                  <Td colSpan={hideActions ? 4 : 5} textAlign="center" py={8} color="fg.muted">
                    No plans found
                  </Td>
                </Tr>
              ) : (
                plans.map((plan) => (
                  <Tr
                    key={plan.id}
                    _hover={{ bg: "gray.50" }}
                    bg={selectedPlans.has(plan.id!) ? "purple.50" : "white"}
                  >
                    <Td>
                      <Checkbox
                        isChecked={selectedPlans.has(plan.id!)}
                        onChange={() => handlePlanSelection(plan.id!)}
                        colorScheme="purple"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Td>
                    <Td
                      fontWeight="medium"
                      color="fg.default"
                      maxW="300px"
                      whiteSpace="normal"
                      wordBreak="break-word"
                    >
                      <Text mb={0}>{plan.name || "Unnamed Plan"}</Text>
                    </Td>
                    <Td
                      color="fg.muted"
                      width="300px"
                      maxW="300px"
                    >
                      <Tooltip
                        label={
                          <Box maxW="400px">
                            <ReactMarkdown>
                              {plan.description || ""}
                            </ReactMarkdown>
                          </Box>
                        }
                        hasArrow
                        placement="top"
                        isDisabled={!plan.description}
                        bg="gray.700"
                        color="white"
                        fontSize="sm"
                      >
                        <Box
                          fontSize="sm"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          cursor={plan.description ? "pointer" : "default"}
                          sx={{
                            "& p": {
                              margin: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }
                          }}
                        >
                          {plan.description === null || plan.description === undefined || plan.description === "" ? "" : plan.description}
                        </Box>
                      </Tooltip>
                    </Td>
                    <Td
                      color="fg.muted"
                    >
                      {plan.author_name || "Unknown"}
                    </Td>
                    <Td
                      color="fg.muted"
                    >
                      {formatDate(plan.created_date_ms)}
                    </Td>
                    {!hideActions && (
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="Open Plan Runs">
                            <IconButton
                              aria-label="Open Plan Runs"
                              icon={<MdDirectionsRun />}
                              size="sm"
                              bg="none"
                              color="grey"
                              _hover={{ color: "green.500" }}
                              onClick={() => window.open(`/plan/${plan.id}/runs`, "_blank")}
                            />
                          </Tooltip>
                          <Tooltip label="Open Plan">
                            <IconButton
                              aria-label="Open Plan"
                              icon={<ExternalLinkIcon />}
                              size="sm"
                              bg="none"
                              color="grey"
                              _hover={{ color: "purple.500" }}
                              onClick={() => window.open(`/plan/${plan.id}`, "_blank")}
                            />
                          </Tooltip>
                          <Tooltip label="Delete Plan">
                            <IconButton
                              aria-label="Delete Plan"
                              icon={<DeleteIcon />}
                              size="sm"
                              bg="none"
                              color="grey"
                              _hover={{ color: "red.500" }}
                              onClick={() => handleDeletePlan(plan.id!)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    )}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {!isLoading && totalRows > 0 && (
        <HStack justify="center" mt={6} spacing={2}>
          {Array.from({ length: Math.ceil(totalRows / pageSize) }, (_, index) => index + 1).map((pageNumber) => (
            <Button
              key={pageNumber}
              size="sm"
              variant={currentPage === pageNumber ? "solid" : "outline"}
              colorScheme={currentPage === pageNumber ? "purple" : "gray"}
              onClick={() => setCurrentPage(pageNumber)}
              minW="40px"
            >
              {pageNumber}
            </Button>
          ))}
        </HStack>
      )}

      {/* Delete Confirmation Modal */}
      <CustomModal
        isOpen={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        title="Are you sure you want to delete this plan?"
        size="sm"
        maxWidth="600px"
        maxHeight="200px"
        showCloseButton={false}
      >
        <ModalFooter>
          <Button colorScheme="red" mr={3} onClick={handleDelete}>
            Delete
          </Button>
          <Button colorScheme="purple" onClick={() => setOpenDeleteModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </CustomModal>

      {/* Dependency Modal */}
      {openDependencyModal && (
        <CustomModal
          isOpen={openDependencyModal}
          onClose={() => setOpenDependencyModal(false)}
          title="Failed to delete, please resolve dependency"
          size="lg"
          maxWidth="800px"
          maxHeight="600px"
        >
          <VStack spacing={4} align="start" p={4}>
            {dependencies && (
              <Box width="100%">
                {dependencies.map((dependency) => (
                  <Text key={dependency.id} fontStyle="italic" fontWeight="bold">{dependency.name}</Text>
                ))}
              </Box>
            )}
          </VStack>
        </CustomModal>
      )}
    </Box>
  );
};

export default LibraryPlansComponent;